import { Node as NodeTreeInterface } from 'react-checkbox-tree';
import { utcToZonedTime } from 'date-fns-tz';

import { uuid } from '@utils/common';
import { dateFormattingWithSlash } from '@utils/date';
import { addClosingBrackets } from '@components/common/FlatTree/utils';

import { TTreeNode } from '@interfaces/cmModule/goldenSettings';
import {
	ICategoryPath,
	IFlatMoTreeNode,
	IListOfTreePresets,
	IMoTreeInitial,
	IPrepareTree,
	ITreeCheckboxOption,
} from '@interfaces/components/tree';
import { IBracketStackInterface } from '@components/common/FlatTree/interface';
import { IConvertConfigFlatTreeParams } from './interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ETreeTypeNode, TTreeTypeNode } from '@enums/tree';

import { timezoneUTC } from '@constants/common';
import { ARRAY_NODE_KEY, FLAT_NODE_PATH_SEPARATOR } from '@components/common/FlatTree/constants';

const TREE_OBJECT_TYPES: TTreeTypeNode[] = [ETreeTypeNode.object, ETreeTypeNode.arrayObjects, ETreeTypeNode.array];

// For readability: for create new object properties I use obj['parameterName'], for existing object.parameterName
export const prepareTree = (tree?: IMoTreeInitial): IPrepareTree | undefined => {
	if (!tree) return;

	return function prepareTreeNode(
		subTree: IMoTreeInitial & Partial<NodeTreeInterface & { id: string }>,
	): IPrepareTree {
		if (subTree.subNodes?.length) {
			subTree['children'] = subTree.subNodes
				.sort((a, b) => {
					const firstType = a.type.name;
					const secondType = b.type.name;

					if (firstType < secondType) {
						return -1;
					}
					if (firstType > secondType) {
						return 1;
					}

					return 0;
				})
				.map((node) => prepareTreeNode(node));
		}

		if (subTree.type.name === ETreeTypeNode.object) {
			subTree['className'] = 'folderClassName';
		}

		subTree['value'] = subTree['id'] =
			subTree.type.name === ETreeTypeNode.object
				? subTree.categoryId
				: subTree.categoryId + subTree.name;
		subTree['label'] = subTree.name;

		return subTree as IPrepareTree;
	}(tree);
};

export const getPaths = (tree: IPrepareTree, ids: Array<string>): ICategoryPath[] => {
	const categoryPaths: Array<ICategoryPath> = [];

	(function preparePath(subTree: IPrepareTree) {
		if (subTree.type.name === ETreeTypeNode.object && subTree?.subNodes?.length) {

			const listOfCheckedParameters = subTree.subNodes
				.filter(({ type }) => type.name === ETreeTypeNode.parameter)
				.map(({ id }) => ids.includes(id));

			const isAllChildrenChecked = !listOfCheckedParameters.length
				? false
				: !listOfCheckedParameters.includes(false);

			if (isAllChildrenChecked) {
				categoryPaths.push({
					categoryId: subTree.categoryId,
					includeAllParams: true,
					params: [],
				});
			} else {
				const checkedNodes = subTree.subNodes.filter(({ id }) => ids.includes(id));

				if (checkedNodes.length) {
					const checkedNames = checkedNodes.map(({ name }) => name);

					categoryPaths.push({
						categoryId: subTree.categoryId,
						includeAllParams: false,
						params: checkedNames,
					});
				}
			}

			subTree.subNodes.forEach((node) => preparePath(node));
		}
	})(tree);

	return categoryPaths;
};

export const preparePresetsTreeOptions = (options: Array<IListOfTreePresets>): Array<TSelectOptionType> => {
	return options.map(option => ({
		label: option.name,
		subText: dateFormattingWithSlash(utcToZonedTime(option.createdAt, timezoneUTC)),
		value: option.id,
		...option,
	}));
};

export const getCheckedIds = ({
	tree,
	paths,
}: {
	tree: IPrepareTree;
	paths: Array<ICategoryPath>;
}): Array<string> => {
	const checkedIds: Array<string> = [];

	(function generateIds(subTree: IPrepareTree) {
		if (subTree.type.name === ETreeTypeNode.object && subTree.children?.length) {
			const listOfParams = subTree.children.filter((item) => item.type.name === ETreeTypeNode.parameter);

			paths.forEach((path) => {
				if (path.categoryId === subTree.id) {
					if (path.includeAllParams) {
						const ids = listOfParams.map((param) => param.categoryId + param.name);

						checkedIds.push(...ids);
					} else {
						const ids = listOfParams
							.filter(({ name }) => path.params.includes(name))
							.map(({ categoryId, name }) => categoryId + name);

						checkedIds.push(...ids);
					}
				}
			});

			subTree.children.forEach((node) => generateIds(node));
		}
	})(tree);

	return checkedIds;
};

export const getCountOfChildren = (subNodes: Array<IMoTreeInitial> | []): number => {
	let count = 0;

	for (const subNode of subNodes) {
		count += 1;

		if (subNode?.subNodes?.length) {
			count += getCountOfChildren(subNode.subNodes);
		}
	}

	return count;
};

const buildCheckboxList = (
	subNode: IMoTreeInitial,
	isSingleSelectMode: boolean,
	isMoIdCheckbox: boolean,
) => {
	const singleCheckboxValue = subNode?.moId || subNode?.categoryId;

	const checkboxList = subNode.moIds?.length
		? [...subNode.moIds]
			.sort((a, b) => Number(a) - Number(b))
			.filter(Boolean)
			.map((item: string, index: number) => {
				return {
					value: item,
					isChecked: !isSingleSelectMode || isSingleSelectMode && index === 0,
					isSelected: true,
					nodeId: item,
				};
			})
		: isMoIdCheckbox && singleCheckboxValue
			? [{
				value: singleCheckboxValue,
				isChecked: true,
				isSelected: true,
				nodeId: singleCheckboxValue,
			}] : undefined;
	return checkboxList;
};

export const createFlatTreePath = (key: string, parentPath?: string) => {
	return parentPath ? `${parentPath}${FLAT_NODE_PATH_SEPARATOR}${key}` : `${key}`;
};

export const buildFlatTree = ({
	data,
	parentPath,
	flatArray = [],
	collapseDepth,
	depth = 0,
	isSingleSelectMode = false,
	defaultCheckboxList = [],
	parentDescription,
	isDefaultNodeChecked = false,
	descriptionParentKey,
	isMoIdCheckbox = false,
	keysParentPath,
	skippedPaths,
}: IConvertConfigFlatTreeParams): IFlatMoTreeNode[] => {
	for (const subNode of data) {
		const uniqueKey = uuid();

		const path = createFlatTreePath(uniqueKey, parentPath);
		const keysPath = createFlatTreePath(subNode.name, keysParentPath);

		const checkboxList = buildCheckboxList(subNode, isSingleSelectMode, isMoIdCheckbox);

		const countOfChildren = subNode?.subNodes ? getCountOfChildren(subNode.subNodes) : 0;

		const isObject = TREE_OBJECT_TYPES.includes(subNode.type.name);
		const description = isObject && subNode.description ? subNode.description : parentDescription;
		const descriptionKey = isObject && subNode.description ? subNode.name : descriptionParentKey;
		const isNodeSkipped = skippedPaths ? !skippedPaths?.includes(path) : isDefaultNodeChecked;

		addFlatTreeNode({
			flatArray,
			subNode,
			isObject,
			path,
			depth,
			keysPath,
			isNodeSkipped,
			collapseDepth,
			uniqueKey,
			countOfChildren,
			isDefaultNodeChecked,
			checkboxList,
			defaultCheckboxList,
			description,
		});

		if (subNode?.subNodes?.length) {
			buildFlatTree({
				data: subNode?.subNodes || [],
				flatArray,
				depth: depth + 1,
				parentPath: path,
				keysParentPath: keysPath,
				parentDescription: description,
				descriptionParentKey: descriptionKey,
				isSingleSelectMode,
				collapseDepth,
				isDefaultNodeChecked,
				skippedPaths,
				isMoIdCheckbox,
			});
		}
	}

	return flatArray;
};

export const filterTree = (node: TTreeNode): TTreeNode | null => {
	node.subNodes = node.subNodes.map(filterTree).filter((item): item is TTreeNode => Boolean(item));

	if (node.subNodes.length === 0 && node.type !== ETreeTypeNode.parameter && node.type !== ETreeTypeNode.array) {
		return null;
	}

	return node;
};

const extractArrayValues = (flatArray: IFlatMoTreeNode[]) => {
	const result: IFlatMoTreeNode[] = [];

	let arrayNodePath = '';
	let arrayValues: string[] = [];

	for (let i = 0; i < flatArray.length; i++) {
		const node = flatArray[i];

		const isChildOfArray = !!arrayNodePath && node.path.startsWith(arrayNodePath) && !node.nodeType;

		if (isChildOfArray && node.value) {
			arrayValues.push(node.value);

			continue;
		} else if (!isChildOfArray && arrayValues.length) {
			result[result.length - 1] = { ...result[result.length - 1], values: arrayValues };

			arrayNodePath = '';
			arrayValues = [];
		}

		// next node also can be array
		if (node.nodeType === ETreeTypeNode.array) {
			arrayNodePath = node.path;
			arrayValues = [];
		}

		result.push(node);
	}

	return result;
};

export const createMoTreeFromFlatMavenirTree = (flatArray: IFlatMoTreeNode[]): TTreeNode | null => {
	let tree = <TTreeNode>{};

	const stack: TTreeNode[] = [];
	const arrayWithValues = extractArrayValues(flatArray);

	for (const node of arrayWithValues) {
		const newNode: TTreeNode = {
			name: node.key,
			type: node.nodeType
				? node.nodeType
				: ETreeTypeNode.parameter,
			value: node.value?.length ? node.value : undefined,
			values: node.values,
			moId: node.categoryId || null,
			categoryId: node.categoryId || '',
			description: node.description,
			path: node.keysPath || '',
			subNodes: [],
		};

		const parent = stack[node.depth - 1];

		if (node.depth === 0) {
			tree = newNode;
			stack.push(newNode);
		} else if (parent?.subNodes) {
			parent.subNodes.push(newNode);

			stack[node.depth] = newNode;
		}
	}

	return filterTree(tree);
};

export const filterCheckedNodes = (nodes: IFlatMoTreeNode[]): IFlatMoTreeNode[] => {
	const stack: IBracketStackInterface[] = [];

	const checkedNodes = nodes.filter((node) => node.isChecked || node.countOfChecked > 0).map((node) => ({ ...node }));

	for (let index = 0; index < checkedNodes.length; index++) {
		addClosingBrackets(checkedNodes, stack, index);
	}

	return checkedNodes;
};

export const convertToMavenirFlatTreeData = (params: IConvertConfigFlatTreeParams): IFlatMoTreeNode[] => {
	const stack: IBracketStackInterface[] = [];

	const formattedTreeData = buildFlatTree(params);

	for (let index = 0; index < formattedTreeData.length; index++) {
		addClosingBrackets(formattedTreeData, stack, index);
	}

	return formattedTreeData;
};

function addFlatTreeNode({
	flatArray,
	subNode,
	isObject,
	path,
	depth,
	isNodeSkipped,
	collapseDepth,
	uniqueKey,
	countOfChildren,
	isDefaultNodeChecked,
	checkboxList,
	defaultCheckboxList,
	description,
	keysPath,
}: {
	flatArray: IFlatMoTreeNode[];
	subNode: IMoTreeInitial;
	isObject: boolean;
	path: string;
	depth: number;
	isNodeSkipped: boolean;
	collapseDepth: number | undefined;
	uniqueKey: string;
	countOfChildren: number;
	isDefaultNodeChecked: boolean;
	checkboxList?: ITreeCheckboxOption[];
	defaultCheckboxList: ITreeCheckboxOption[];
	description: string | undefined;
	keysPath?: string;
}) {
	flatArray.push({
		key: subNode.name,
		value: subNode?.value,
		dbId: subNode.dbId,
		nodeType: isObject
			? subNode.type.name
			: undefined,
		path,
		keysPath,
		depth,
		values: subNode?.values,
		tags: subNode?.tags,
		isChecked: isNodeSkipped,
		isCollapsed: !!collapseDepth && depth >= collapseDepth,
		isHiddenChildren: !!collapseDepth && depth >= collapseDepth - 1,
		categoryId: subNode.moId || undefined,
		id: uniqueKey,
		brackets: undefined,
		nodeIdsList: [],
		selectedNodeIds: [],
		countOfChildren: countOfChildren,
		countOfChecked: isDefaultNodeChecked ? countOfChildren : 0,
		checkboxList: checkboxList || defaultCheckboxList,
		description,
		order: 0,
	});

	if (subNode?.values?.length && subNode.type.name === ETreeTypeNode.array) {
		const values: IFlatMoTreeNode[] = subNode.values.map((value) => ({
			key: ARRAY_NODE_KEY,
			value,
			path: `${path}${FLAT_NODE_PATH_SEPARATOR}${value}`,
			keysPath: `${keysPath}${FLAT_NODE_PATH_SEPARATOR}${value}`,
			id: uuid(),
			isChecked: isNodeSkipped,
			description,
			dbId: subNode.dbId,
			isCollapsed: !!collapseDepth && depth >= collapseDepth,
			countOfChildren,
			countOfChecked: 0,
			checkboxList: [],
			depth: depth + 1,
			isHiddenChildren: false,
			order: 0,
		}));

		flatArray.push(...values);
	}
}

